import { useState, useEffect } from "react";
import axios from "axios";
import { Save, Loader2, CheckCircle2, Sparkles, Lightbulb } from "lucide-react";
import LemonBotPanel from "./LemonBotPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Per-section "Next step guidance" — surfaces a 1-line instruction at the top
// of each capstone field so students know what to write WITHOUT opening
// LemonBot. Keyed by the same `capstone_field` slug used on the model.
const FIELD_GUIDANCE = {
  ms_business_idea:
    "Start with: business name → what it does (1 line) → who it helps. Be specific.",
  ms_customer_profile:
    'Pick ONE imaginary customer. Give them a name, age, and the EXACT problem they have.',
  ms_brand:
    "Choose a name + tagline + 2 colors + 3 voice words (e.g., friendly, zesty, honest).",
  ms_money_plan:
    "Cost per cup → price per cup → profit per cup → weekly cup goal → what you'll do with profit.",
  ms_marketing:
    "1-sentence brand story + 2 channels (where you'll reach people) + 1 first-time-customer promo.",
  ms_operations:
    "Who's on your team + supplies you need + supplier source + weekly hours.",
  ms_launch:
    "Pick a launch date + first event + your first 3 customers + 1 success metric for week 1.",
};

export default function CapstoneBuilder({ module, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const field = module?.capstone_field;
  const prompt = module?.capstone_prompt;
  const label = module?.capstone_label;

  useEffect(() => {
    if (!field) return;
    const load = async () => {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("student_token");
        const res = await axios.get(`${API}/capstone/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existing = res.data?.fields?.[field] || "";
        setValue(existing);
        if (existing) setSaved(true);
      } catch (e) { /* ignore */ }
    };
    load();
  }, [field]);

  const handleSave = async () => {
    if (!field || !value.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("student_token");
      await axios.post(`${API}/capstone/progress`, { field, value: value.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      if (onSaved) onSaved(field, value.trim());
    } catch (e) {
      console.error("Failed to save capstone:", e);
    } finally {
      setSaving(false);
    }
  };

  if (!field || !prompt) return null;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-6 mb-6" data-testid="capstone-builder">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">🧱</span>
        <h3 className="text-lg font-bold text-gray-800">Build Your Business: {label}</h3>
        <span
          className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-200 text-amber-900 rounded-full px-2 py-0.5"
          title="LemonBot can help refine this section"
          data-testid="ai-assist-marker"
        >
          <Sparkles className="w-3 h-3" />
          AI Assist
        </span>
        {saved && <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />}
      </div>
      <p className="text-sm text-gray-600 mb-3">{prompt}</p>

      {/* Next-step guidance — visible without opening LemonBot. */}
      {FIELD_GUIDANCE[field] && (
        <div
          className="flex items-start gap-2 bg-white/70 border border-amber-300 rounded-lg p-3 mb-3 text-sm"
          data-testid={`capstone-guidance-${field}`}
        >
          <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-amber-900">
            <span className="font-semibold">Next step:</span>{" "}
            {FIELD_GUIDANCE[field]}
          </div>
        </div>
      )}

      <textarea
        value={value}
        onChange={(e) => { setValue(e.target.value); setSaved(false); }}
        rows={4}
        className="w-full px-4 py-3 rounded-lg border-2 border-amber-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none text-sm resize-none bg-white"
        placeholder="Type your response here..."
        data-testid="capstone-input"
      />
      <div className="flex justify-end mt-3">
        <button
          onClick={handleSave}
          disabled={saving || !value.trim()}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all"
          data-testid="capstone-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved ? "Saved" : "Save Progress"}
        </button>
      </div>

      {/* LemonBot section coach — refines, never replaces the student's thinking */}
      <div className="mt-4">
        <LemonBotPanel
          activity={`business_plan_builder:${field || "section"}`}
          moduleId={module?.id}
          moduleTitle={module?.title}
          title={`LemonBot · ${label} coach`}
          subtitle="I'll help you refine this section — I won't write it for you"
          extraContext={
            value.trim()
              ? `The student's current draft for "${label}" is: "${value.trim().slice(0, 600)}"`
              : `The student is starting "${label}" and hasn't written anything yet.`
          }
          quickActions={[
            {
              intent: "business_plan_help",
              label: "Coach my draft",
              prompt: `Look at my current draft for "${label}" and ask me 2 short questions that will sharpen it. Don't rewrite it for me.`,
            },
            {
              intent: "give_example",
              label: "Show example",
              prompt: `Give me ONE quick example of what a strong "${label}" section sounds like for a student lemonade business — keep it under 60 words.`,
            },
            {
              intent: "explain_easier",
              label: "Stuck",
              prompt: `I'm stuck on "${label}". What's a 5-minute step I can do right now to get unstuck?`,
            },
          ]}
        />
      </div>
    </div>
  );
}
